import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";

export interface PatternSnapshotItem {
  content: string;
  type: string;
  n: number;
  k: number;
  decay_days: number;
  effective_score: number;
  promotion_state: string;
  promotion_eligible: boolean;
  context: string[];
  updated_at: string;
}

interface PatternListResponse {
  patterns: PatternSnapshotItem[];
  total: number;
  has_more: boolean;
}

export interface LeverageTrendPoint {
  week: string;
  avg_score: number;
  pattern_count: number;
}

export interface PatternHealthData {
  leverage_trend: LeverageTrendPoint[];
  retrieval_precision: number;
  total_patterns: number;
  evaluated_patterns: number;
  decay_count: number;
}

export function usePatternHealth(projectSlug: string | null) {
  const query = useQuery({
    queryKey: queryKeys.metrics.patternHealth(projectSlug ?? ""),
    queryFn: async () => {
      const [listRes, healthRes] = await Promise.all([
        api<PatternListResponse>(
          `/api/v2/projects/${projectSlug}/memory/patterns`,
        ),
        api<PatternHealthData>(
          `/api/v2/projects/${projectSlug}/memory/pattern-health`,
        ),
      ]);
      return { patterns: listRes.patterns, health: healthRes };
    },
    enabled: projectSlug !== null,
  });

  return {
    patterns: query.data?.patterns ?? [],
    health: query.data?.health ?? null,
    loading: query.isLoading,
    error: query.error ? "Failed to load pattern health data" : "",
  };
}
